// workflowMerge — pure helpers för "Import JSON" i append-läge.
//
// Tidigare ersatte import hela canvas:en (loadState). Append-läget låter
// users bygga ett flöde i segment: importera, dra till plats, importera
// nästa, osv. Allt importerat content wrapas i en ny ytter-grupp så
// användaren kan flytta hela batchen som en enhet.
//
// Två huvud-helpers:
//   mergeIntoSheet  — single-sheet import → existing active sheet
//   mergeIntoState  — multi-sheet import   → new tabs after current
//
// Båda gör:
//   1. Generera fresh UUIDs för alla importerade block / groups / connectors
//   2. Skriv om interna referenser via en (oldId → newId)-karta så att
//      connector.fromId/toId, group.blockIds, group.parentGroupId,
//      cross-sheet-connector endpoints fortsätter peka rätt.
//   3. Offset:a block-positions så imported.bbox.topLeft hamnar
//      `current.bbox.bottomRight + GAP` (ned-och-höger-diagonal). collapsedX/Y
//      på groups följer samma offset.
//   4. Wrap:a alla importerade blocks i en ytter-grupp ("Imported batch")
//      som user kan dra runt.

import type {
  WorkflowState,
  WorkflowBlock,
  Connector,
  BlockGroup,
  WorkflowSheet,
  CrossSheetConnector,
  MultiSheetWorkflowState,
} from '@/types/workflow';
import { isMultiSheetState, wrapSingleSheet } from '@/types/workflow';

const APPEND_GAP_PX = 200;
const IMPORTED_BATCH_COLOR = '#8b5cf6';
const IMPORTED_BATCH_NAME = 'Imported';

interface BBox { minX: number; maxX: number; minY: number; maxY: number; }

function bbox(blocks: WorkflowBlock[]): BBox | null {
  if (blocks.length === 0) return null;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const b of blocks) {
    if (b.x < minX) minX = b.x;
    if (b.x > maxX) maxX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.y > maxY) maxY = b.y;
  }
  return { minX, maxX, minY, maxY };
}

function computeOffset(currentBlocks: WorkflowBlock[], imported: WorkflowBlock[]): { dx: number; dy: number } {
  const cur = bbox(currentBlocks);
  const imp = bbox(imported);
  if (!cur || !imp) return { dx: 0, dy: 0 };
  // Ned-och-höger-diagonal: imported.topLeft hamnar vid current.bottomRight + gap.
  return {
    dx: cur.maxX + APPEND_GAP_PX - imp.minX,
    dy: cur.maxY + APPEND_GAP_PX - imp.minY,
  };
}

export interface MergeResult {
  sheet: WorkflowSheet;
  /** Mappning oldId → newId för debugging / undo. Innehåller block-, group-
   *  och connector-id:n. */
  idMap: Record<string, string>;
  /** Id på den ytter-grupp vi wrapade importen i (om imported hade blocks).
   *  Användbart för callers som vill highlight:a / scroll:a till den. */
  wrapperGroupId?: string;
}

/**
 * Merga ett single-sheet-imported (WorkflowState) in i en existerande sheet.
 * Returnerar ny sheet — caller-ansvar att stoppa in den i state-trädet.
 */
export function mergeIntoSheet(
  current: WorkflowSheet,
  imported: WorkflowState,
): MergeResult {
  const idMap: Record<string, string> = {};
  for (const b of imported.blocks) idMap[b.id] = crypto.randomUUID();
  for (const g of imported.groups) idMap[g.id] = crypto.randomUUID();
  for (const c of imported.connectors) idMap[c.id] = crypto.randomUUID();

  const { dx, dy } = computeOffset(current.blocks, imported.blocks);

  const newBlocks: WorkflowBlock[] = imported.blocks.map((b) => ({
    ...b,
    id: idMap[b.id],
    x: b.x + dx,
    y: b.y + dy,
  }));

  const newConnectors: Connector[] = imported.connectors.map((c) => ({
    ...c,
    id: idMap[c.id],
    fromId: idMap[c.fromId] ?? c.fromId,
    toId: idMap[c.toId] ?? c.toId,
  }));

  const newGroups: BlockGroup[] = imported.groups.map((g) => ({
    ...g,
    id: idMap[g.id],
    blockIds: g.blockIds.map((bid) => idMap[bid] ?? bid),
    ...(g.parentGroupId ? { parentGroupId: idMap[g.parentGroupId] ?? g.parentGroupId } : {}),
    ...(g.collapsedX != null ? { collapsedX: g.collapsedX + dx } : {}),
    ...(g.collapsedY != null ? { collapsedY: g.collapsedY + dy } : {}),
    ...(g.boundsLeft != null ? { boundsLeft: g.boundsLeft + dx } : {}),
    ...(g.boundsRight != null ? { boundsRight: g.boundsRight + dx } : {}),
    ...(g.boundsTop != null ? { boundsTop: g.boundsTop + dy } : {}),
    ...(g.boundsBottom != null ? { boundsBottom: g.boundsBottom + dy } : {}),
  }));

  // Wrap:a alla importerade block i en ytter-grupp så user kan flytta
  // hela batchen som en enhet (user-spec). Skippa wrappningen om importen
  // var tom.
  let wrapperGroupId: string | undefined;
  if (newBlocks.length > 0) {
    wrapperGroupId = crypto.randomUUID();
    const wrapper: BlockGroup = {
      id: wrapperGroupId,
      blockIds: newBlocks.map((b) => b.id),
      color: IMPORTED_BATCH_COLOR,
      name: IMPORTED_BATCH_NAME,
      collapsed: false,
    };
    newGroups.push(wrapper);
  }

  return {
    sheet: {
      ...current,
      blocks: [...current.blocks, ...newBlocks],
      connectors: [...current.connectors, ...newConnectors],
      groups: [...current.groups, ...newGroups],
    },
    idMap,
    wrapperGroupId,
  };
}

export interface MergeStateResult {
  state: MultiSheetWorkflowState;
  /** Globalt idMap över alla appendade sheets — block/group/connector/sheet-id. */
  idMap: Record<string, string>;
  /** Id:n på de ytter-grupper vi wrapade importen i, en per nytillagd sheet. */
  wrapperGroupIds: string[];
  /** Sant om tier-gränsen för max-sheets klippte några importerade sheets. */
  truncated: boolean;
  /** Antal cross-sheet-connectors som dropp:ades pga truncation. */
  droppedCrossLinks: number;
}

/**
 * Merga ett (multi- eller single-)imported state i existerande
 * MultiSheetWorkflowState. Multi-sheet-imports läggs till som NYA flikar
 * efter de existerande. Single-sheet-imports mergas i aktiv sheet.
 *
 * `maxSheets` (om given) clampar hur många sheets append:en får skapa —
 * dropped sheets + deras cross-links rapporteras via `truncated` /
 * `droppedCrossLinks`.
 */
export function mergeIntoState(
  current: MultiSheetWorkflowState,
  importedRaw: WorkflowState | MultiSheetWorkflowState,
  opts?: { maxSheets?: number },
): MergeStateResult {
  // Single-sheet-import: append i active sheet, ingen ny flik.
  if (!isMultiSheetState(importedRaw)) {
    const activeIndex = current.activeSheetIndex;
    const active = current.sheets[activeIndex];
    if (!active) {
      // Defensiv: tomt multi-state — wrap:a importen som första sheet.
      const wrapped = wrapSingleSheet(importedRaw);
      return mergeIntoState(current, wrapped, opts);
    }
    const merged = mergeIntoSheet(active, importedRaw);
    const newSheets = [...current.sheets];
    newSheets[activeIndex] = merged.sheet;
    return {
      state: { ...current, sheets: newSheets },
      idMap: merged.idMap,
      wrapperGroupIds: merged.wrapperGroupId ? [merged.wrapperGroupId] : [],
      truncated: false,
      droppedCrossLinks: 0,
    };
  }

  // Multi-sheet-import: lägg till sheets som nya flikar.
  const imported = importedRaw;
  const sheetOffset = current.sheets.length;
  const cap = opts?.maxSheets ?? Infinity;
  const room = Math.max(0, cap - sheetOffset);
  const acceptedSheets = imported.sheets.slice(0, room);
  const truncated = acceptedSheets.length < imported.sheets.length;

  // Ett globalt idMap så cross-sheet-connectors kan resolva endpoints
  // oavsett vilken sheet block:et bor i. Wrapper-id:n samlas separat.
  const idMap: Record<string, string> = {};
  const wrapperGroupIds: string[] = [];
  const newSheets: WorkflowSheet[] = [];

  for (const sheet of acceptedSheets) {
    // Pre-fyll idMap för denna sheet så mergeIntoSheet:s genererade
    // id:n inte kolliderar.
    for (const b of sheet.blocks) idMap[b.id] = crypto.randomUUID();
    for (const g of sheet.groups) idMap[g.id] = crypto.randomUUID();
    for (const c of sheet.connectors) idMap[c.id] = crypto.randomUUID();
  }

  for (const sheet of acceptedSheets) {
    // Wrap:a varje sheet:s blocks i ytter-grupp + offset:a positions
    // identiskt med single-sheet-fallet (men mot en TOM "current" så
    // imported behåller sina relativa positioner). MergeIntoSheet
    // accepterar emptySheet → ingen offset.
    const emptySheet: WorkflowSheet = {
      id: crypto.randomUUID(),
      name: sheet.name,
      blocks: [],
      connectors: [],
      groups: [],
    };
    // Vi kan inte återanvända mergeIntoSheet rakt av eftersom den genererar
    // egna UUIDs — vi har redan pre-genererat dem i idMap. Inline-merge i stället.
    const newBlocks: WorkflowBlock[] = sheet.blocks.map((b) => ({
      ...b,
      id: idMap[b.id],
    }));
    const newConnectors: Connector[] = sheet.connectors.map((c) => ({
      ...c,
      id: idMap[c.id],
      fromId: idMap[c.fromId] ?? c.fromId,
      toId: idMap[c.toId] ?? c.toId,
    }));
    const newGroups: BlockGroup[] = sheet.groups.map((g) => ({
      ...g,
      id: idMap[g.id],
      blockIds: g.blockIds.map((bid) => idMap[bid] ?? bid),
      ...(g.parentGroupId ? { parentGroupId: idMap[g.parentGroupId] ?? g.parentGroupId } : {}),
    }));

    let wrapperGroupId: string | undefined;
    if (newBlocks.length > 0) {
      wrapperGroupId = crypto.randomUUID();
      newGroups.push({
        id: wrapperGroupId,
        blockIds: newBlocks.map((b) => b.id),
        color: IMPORTED_BATCH_COLOR,
        name: IMPORTED_BATCH_NAME,
        collapsed: false,
      });
      wrapperGroupIds.push(wrapperGroupId);
    }

    newSheets.push({
      ...emptySheet,
      blocks: newBlocks,
      connectors: newConnectors,
      groups: newGroups,
    });
  }

  // Cross-sheet-connectors: shifta sheet-index:n med sheetOffset, drop
  // länkar som pekar utanför det accepterade område:t.
  const acceptedRange = (idx: number) => idx >= 0 && idx < acceptedSheets.length;
  let droppedCrossLinks = 0;
  const newCSCs: CrossSheetConnector[] = [];
  for (const csc of imported.crossSheetConnectors ?? []) {
    if (!acceptedRange(csc.fromSheetIndex) || !acceptedRange(csc.toSheetIndex)) {
      droppedCrossLinks++;
      continue;
    }
    newCSCs.push({
      ...csc,
      id: crypto.randomUUID(),
      fromSheetIndex: csc.fromSheetIndex + sheetOffset,
      toSheetIndex: csc.toSheetIndex + sheetOffset,
      fromBlockId: idMap[csc.fromBlockId] ?? csc.fromBlockId,
      toBlockId: idMap[csc.toBlockId] ?? csc.toBlockId,
    });
  }

  return {
    state: {
      ...current,
      sheets: [...current.sheets, ...newSheets],
      crossSheetConnectors: [...(current.crossSheetConnectors ?? []), ...newCSCs],
    },
    idMap,
    wrapperGroupIds,
    truncated,
    droppedCrossLinks,
  };
}
